/**
 * NewsSlideEditor.jsx — src/components/News/NewsSlideEditor.jsx
 * Редактор одного слайда: формат, картинки, текст (TipTap), оформление, кнопка.
 * Монтируется с key={slide.id} — при переключении слайда пересоздаётся.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Button, Chip, IconButton, Input, Option, Select, Sheet, Stack, Switch, Tooltip, Typography,
} from '@mui/joy';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';

import FormatBoldRoundedIcon from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRoundedIcon from '@mui/icons-material/FormatItalicRounded';
import FormatUnderlinedRoundedIcon from '@mui/icons-material/FormatUnderlinedRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import FormatAlignLeftRoundedIcon from '@mui/icons-material/FormatAlignLeftRounded';
import FormatAlignCenterRoundedIcon from '@mui/icons-material/FormatAlignCenterRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import WallpaperRoundedIcon from '@mui/icons-material/WallpaperRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import {
    APIDeleteNewsSlide, APIDeleteNewsSlideBackground, APIDeleteNewsSlideImage,
    APIUpdateNewsSlide, APIUploadNewsEditorImage, APIUploadNewsSlideBackground,
    APIUploadNewsSlideImages,
} from '../../api';

const SAVE_DELAY = 1200;

const EDITOR_CSS = `
.news-slide-editor { font-size:15px; line-height:1.65; color:#1a1a2e; outline:none; min-height:160px; }
.news-slide-editor p { margin:0 0 0.8em; }
.news-slide-editor h1 { font-size:1.5rem; font-weight:800; margin:0.8em 0 0.35em; }
.news-slide-editor h2 { font-size:1.22rem; font-weight:800; margin:0.7em 0 0.3em; }
.news-slide-editor h3 { font-size:1.05rem; font-weight:700; margin:0.6em 0 0.3em; }
.news-slide-editor ul,.news-slide-editor ol { padding-left:1.4em; margin:0.5em 0 0.8em; }
.news-slide-editor li { margin-bottom:0.25em; }
.news-slide-editor blockquote { border-left:3px solid #8b5cf6; margin:0.8em 0; padding:0.5em 0.9em; background:rgba(139,92,246,0.06); border-radius:0 8px 8px 0; }
.news-slide-editor hr { border:none; border-top:2px solid rgba(99,102,241,0.15); margin:1.2em 0; }
.news-slide-editor img { max-width:100%; border-radius:10px; margin:0.4em 0; display:block; }
.news-slide-editor a { color:#6366f1; text-decoration:underline; }
.news-slide-editor p.is-editor-empty:first-child::before { color:#bbb; content:attr(data-placeholder); float:left; height:0; pointer-events:none; }
.news-slide-editor *:focus { outline:none !important; }
`;

const labelSx = { fontSize: '0.72rem', fontWeight: 700, color: 'text.tertiary', mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.4px' };

function TB({ children, onClick, active, title }) {
    return (
        <Tooltip title={title} size="sm">
            <IconButton
                size="sm"
                variant={active ? 'solid' : 'plain'}
                color={active ? 'primary' : 'neutral'}
                onClick={onClick}
                sx={{ minHeight: 28, minWidth: 28 }}
            >
                {children}
            </IconButton>
        </Tooltip>
    );
}


export default function NewsSlideEditor({ slide, token, notify, onUpdated, onDeleted }) {
    const [local, setLocal] = useState(slide);
    const [uploading, setUploading] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const pendingRef = useRef({});
    const timerRef = useRef(null);
    const bgInputRef = useRef(null);
    const imagesInputRef = useRef(null);
    const inlineImageRef = useRef(null);

    useEffect(() => { setLocal(slide); }, [slide?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Сохранение с задержкой ───────────────────────────────────────────── */
    const flush = useCallback(async () => {
        const patch = pendingRef.current;
        pendingRef.current = {};
        if (!Object.keys(patch).length) return;

        const res = await APIUpdateNewsSlide(slide.id, patch, token);
        if (res?._error) {
            notify?.(res?.duration_seconds?.[0] || res?.error || 'Слайд не сохранился', 'danger');
            return;
        }
        onUpdated?.(res);
    }, [slide?.id, token, notify, onUpdated]);

    const patch = useCallback((data) => {
        setLocal(prev => ({ ...prev, ...data }));
        pendingRef.current = { ...pendingRef.current, ...data };
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(flush, SAVE_DELAY);
    }, [flush]);

    useEffect(() => () => { clearTimeout(timerRef.current); flush(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── TipTap ───────────────────────────────────────────────────────────── */
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            Image.configure({ inline: false, allowBase64: false }),
            Link.configure({ openOnClick: false }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            Placeholder.configure({ placeholder: 'Текст слайда…' }),
        ],
        content: slide?.content || '',
        onUpdate: ({ editor }) => patch({ content: editor.getHTML() }),
    });

    /* ── Загрузка картинок ────────────────────────────────────────────────── */
    const onBackgroundSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setUploading(true);
        const res = await APIUploadNewsSlideBackground(slide.id, file, token);
        setUploading(false);
        if (res?._error) { notify?.(res?.error || 'Не удалось загрузить фон', 'danger'); return; }
        setLocal(res);
        onUpdated?.(res);
    };

    const removeBackground = async () => {
        const res = await APIDeleteNewsSlideBackground(slide.id, token);
        if (res?._error) { notify?.('Не удалось убрать фон', 'danger'); return; }
        setLocal(res);
        onUpdated?.(res);
    };

    const onImagesSelected = async (e) => {
        const files = e.target.files;
        e.target.value = '';
        if (!files?.length) return;
        setUploading(true);
        const res = await APIUploadNewsSlideImages(slide.id, files, token);
        setUploading(false);
        if (res?._error) { notify?.(res?.error || 'Не удалось загрузить картинки', 'danger'); return; }
        const next = { ...local, images: [...(local.images || []), ...res] };
        setLocal(next);
        onUpdated?.(next);
    };

    const removeImage = async (imageId) => {
        const res = await APIDeleteNewsSlideImage(imageId, token);
        if (res?._error) { notify?.('Не удалось удалить картинку', 'danger'); return; }
        const next = { ...local, images: (local.images || []).filter(i => i.id !== imageId) };
        setLocal(next);
        onUpdated?.(next);
    };

    const onInlineImageSelected = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !editor) return;
        setUploading(true);
        const res = await APIUploadNewsEditorImage(file, token);
        setUploading(false);
        if (res?._error || !res?.url) { notify?.('Не удалось загрузить картинку', 'danger'); return; }
        editor.chain().focus().setImage({ src: res.url }).run();
    };

    const addLink = () => {
        const url = window.prompt('URL ссылки:');
        if (url) editor?.chain().focus().setLink({ href: url }).run();
    };

    const handleDelete = async () => {
        const res = await APIDeleteNewsSlide(slide.id, token);
        if (res?._error) { notify?.('Не удалось удалить слайд', 'danger'); return; }
        onDeleted?.(slide.id);
    };

    const isFull = local.layout === 'full_image';

    return (
        <Sheet sx={{ borderRadius: '18px', border: '1px solid', borderColor: 'divider', p: 2.2 }}>
            <style>{EDITOR_CSS}</style>
            <input ref={bgInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onBackgroundSelected} />
            <input ref={imagesInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onImagesSelected} />
            <input ref={inlineImageRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onInlineImageSelected} />

            {/* Шапка слайда */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.6 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontWeight: 800 }}>Слайд</Typography>
                    {uploading && <Chip size="sm" variant="soft" color="primary">Загрузка…</Chip>}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Активен</Typography>
                    <Switch size="sm" checked={!!local.is_active} onChange={(e) => patch({ is_active: e.target.checked })} />
                    {confirmDelete ? (
                        <Stack direction="row" spacing={0.5}>
                            <Button size="sm" color="danger" onClick={handleDelete}>Удалить</Button>
                            <IconButton size="sm" variant="soft" onClick={() => setConfirmDelete(false)}>
                                <CloseRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Stack>
                    ) : (
                        <Tooltip title="Удалить слайд" size="sm">
                            <IconButton size="sm" variant="soft" color="danger" onClick={() => setConfirmDelete(true)}>
                                <DeleteForeverRoundedIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            {/* Формат */}
            <Typography sx={labelSx}>Формат</Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 1.6 }}>
                <Button
                    fullWidth
                    variant={isFull ? 'solid' : 'soft'}
                    color={isFull ? 'primary' : 'neutral'}
                    startDecorator={<WallpaperRoundedIcon />}
                    onClick={() => patch({ layout: 'full_image' })}
                >
                    Фул картинка
                </Button>
                <Button
                    fullWidth
                    variant={!isFull ? 'solid' : 'soft'}
                    color={!isFull ? 'primary' : 'neutral'}
                    startDecorator={<ImageRoundedIcon />}
                    onClick={() => patch({ layout: 'standard' })}
                >
                    Стандарт
                </Button>
            </Stack>

            {/* Медиа */}
            {isFull ? (
                <>
                    <Typography sx={labelSx}>Фоновая картинка</Typography>
                    <Box
                        onClick={() => bgInputRef.current?.click()}
                        sx={{
                            position: 'relative', height: 150, borderRadius: '14px', cursor: 'pointer',
                            border: '1px dashed', borderColor: 'divider', mb: 1.6,
                            background: local.background_image_url ? `url(${local.background_image_url}) center/cover no-repeat` : 'transparent',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            '&:hover': { borderColor: '#6366f1' },
                        }}
                    >
                        {!local.background_image_url && (
                            <Stack alignItems="center" spacing={0.5}>
                                <AddPhotoAlternateRoundedIcon sx={{ color: 'text.tertiary' }} />
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Загрузить фон</Typography>
                            </Stack>
                        )}
                        {local.background_image_url && (
                            <IconButton
                                size="sm" color="danger" variant="solid"
                                onClick={(e) => { e.stopPropagation(); removeBackground(); }}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                            >
                                <CloseRoundedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>

                    <Typography sx={labelSx}>Затемнение фона: {Math.round((local.overlay_opacity ?? 0.45) * 100)}%</Typography>
                    <Box
                        component="input"
                        type="range" min="0" max="90" step="5"
                        value={Math.round((local.overlay_opacity ?? 0.45) * 100)}
                        onChange={(e) => patch({ overlay_opacity: Number(e.target.value) / 100 })}
                        sx={{ width: '100%', mb: 1.6 }}
                    />
                </>
            ) : (
                <>
                    <Typography sx={labelSx}>Картинки слайда</Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.6 }}>
                        {(local.images || []).map(img => (
                            <Box key={img.id} sx={{
                                position: 'relative', width: 92, height: 72, borderRadius: '10px',
                                overflow: 'hidden', border: '1px solid', borderColor: 'divider',
                                background: `url(${img.url}) center/cover no-repeat`,
                            }}>
                                <IconButton
                                    size="sm" color="danger" variant="solid"
                                    onClick={() => removeImage(img.id)}
                                    sx={{ position: 'absolute', top: 2, right: 2, minHeight: 22, minWidth: 22 }}
                                >
                                    <CloseRoundedIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Box>
                        ))}
                        <Box
                            onClick={() => imagesInputRef.current?.click()}
                            sx={{
                                width: 92, height: 72, borderRadius: '10px', cursor: 'pointer',
                                border: '1px dashed', borderColor: 'divider',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                '&:hover': { borderColor: '#6366f1' },
                            }}
                        >
                            <AddPhotoAlternateRoundedIcon sx={{ color: 'text.tertiary' }} />
                        </Box>
                    </Stack>
                </>
            )}

            {/* Тексты */}
            <Typography sx={labelSx}>Заголовок слайда</Typography>
            <Input value={local.title || ''} onChange={(e) => patch({ title: e.target.value })} sx={{ mb: 1.4 }} />

            <Typography sx={labelSx}>Короткий текст (свёрнутый вид)</Typography>
            <Input
                value={local.short_text || ''}
                onChange={(e) => patch({ short_text: e.target.value })}
                placeholder="Что видно до нажатия «Подробнее»"
                sx={{ mb: 1.4 }}
            />

            <Typography sx={labelSx}>Текст слайда</Typography>
            <Sheet sx={{ borderRadius: '14px', border: '1px solid', borderColor: 'divider', mb: 1.6, overflow: 'hidden' }}>
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.3,
                    px: 1, py: 0.6, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8f8ff',
                }}>
                    <TB title="H2" active={editor?.isActive('heading', { level: 2 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>
                        <Box sx={{ fontSize: '0.68rem', fontWeight: 800 }}>H2</Box>
                    </TB>
                    <TB title="H3" active={editor?.isActive('heading', { level: 3 })} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>
                        <Box sx={{ fontSize: '0.68rem', fontWeight: 800 }}>H3</Box>
                    </TB>
                    <TB title="Жирный" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}><FormatBoldRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Курсив" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}><FormatItalicRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Подчёркнутый" active={editor?.isActive('underline')} onClick={() => editor?.chain().focus().toggleUnderline().run()}><FormatUnderlinedRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Маркированный список" active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()}><FormatListBulletedRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Нумерованный список" active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()}><FormatListNumberedRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Цитата" active={editor?.isActive('blockquote')} onClick={() => editor?.chain().focus().toggleBlockquote().run()}><FormatQuoteRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="По левому краю" active={editor?.isActive({ textAlign: 'left' })} onClick={() => editor?.chain().focus().setTextAlign('left').run()}><FormatAlignLeftRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="По центру" active={editor?.isActive({ textAlign: 'center' })} onClick={() => editor?.chain().focus().setTextAlign('center').run()}><FormatAlignCenterRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Разделитель" onClick={() => editor?.chain().focus().setHorizontalRule().run()}><HorizontalRuleRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Ссылка" active={editor?.isActive('link')} onClick={addLink}><LinkRoundedIcon sx={{ fontSize: 16 }} /></TB>
                    <TB title="Картинка в текст" onClick={() => inlineImageRef.current?.click()}><ImageRoundedIcon sx={{ fontSize: 16 }} /></TB>
                </Box>
                <Box sx={{ p: 2 }}>
                    <EditorContent editor={editor} className="news-slide-editor" />
                </Box>
            </Sheet>

            {/* Оформление и кнопка */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.4 }}>
                <Box>
                    <Typography sx={labelSx}>Длительность, сек</Typography>
                    <Input
                        type="number"
                        value={local.duration_seconds ?? 15}
                        onChange={(e) => patch({ duration_seconds: Math.min(60, Math.max(3, Number(e.target.value) || 15)) })}
                        slotProps={{ input: { min: 3, max: 60 } }}
                    />
                </Box>

                {!isFull && (
                    <>
                        <Box>
                            <Typography sx={labelSx}>Цвет фона</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box component="input" type="color" value={local.background_color || '#FFFFFF'}
                                     onChange={(e) => patch({ background_color: e.target.value })}
                                     sx={{ width: 44, height: 34, border: 'none', background: 'none', cursor: 'pointer', p: 0 }} />
                                <Input value={local.background_color || ''} onChange={(e) => patch({ background_color: e.target.value })} sx={{ flex: 1 }} />
                            </Stack>
                        </Box>
                        <Box>
                            <Typography sx={labelSx}>Цвет текста</Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Box component="input" type="color" value={local.text_color || '#111111'}
                                     onChange={(e) => patch({ text_color: e.target.value })}
                                     sx={{ width: 44, height: 34, border: 'none', background: 'none', cursor: 'pointer', p: 0 }} />
                                <Input value={local.text_color || ''} onChange={(e) => patch({ text_color: e.target.value })} sx={{ flex: 1 }} />
                            </Stack>
                        </Box>
                    </>
                )}

                <Box>
                    <Typography sx={labelSx}>Текст кнопки (необязательно)</Typography>
                    <Input value={local.button_text || ''} onChange={(e) => patch({ button_text: e.target.value })} placeholder="Открыть раздел" />
                </Box>
                <Box>
                    <Typography sx={labelSx}>Ссылка кнопки</Typography>
                    <Input value={local.button_url || ''} onChange={(e) => patch({ button_url: e.target.value })} placeholder="/payments или https://…" />
                </Box>
            </Box>
        </Sheet>
    );
}